import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Sprint 97 — typed wrappers for the super-admin payout callables.
 * Server re-validates with its own Zod; these wrappers give the
 * dashboard fast feedback before a round-trip.
 */

const RecipientKindSchema = z.enum(['agent', 'recruiter', 'tenant']);

// ── runPayoutBatch ─────────────────────────────────────────────────────

export const RunPayoutBatchInputSchema = z.object({
  kind: RecipientKindSchema,
  brandId: z.string().min(1),
  periodEndMs: z.number().int().positive().optional(),
  thresholdSenOverride: z.number().int().nonnegative().optional(),
});
export type RunPayoutBatchInput = z.infer<typeof RunPayoutBatchInputSchema>;

export const RunPayoutBatchOutputSchema = z.object({
  scanned: z.number().int().nonnegative(),
  paidOut: z.number().int().nonnegative(),
  totalGrossSen: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({
      recipientId: z.string(),
      reason: z.enum([
        'below_threshold',
        'no_destination',
        'inactive',
        'no_eligible_entries',
        'kyc_unverified',
      ]),
    }),
  ),
  payouts: z.array(
    z.object({
      recipientId: z.string(),
      payoutId: z.string(),
      grossSen: z.number().int().nonnegative(),
      itemCount: z.number().int().nonnegative(),
    }),
  ),
});
export type RunPayoutBatchOutput = z.infer<typeof RunPayoutBatchOutputSchema>;

const runFn: HttpsCallable<RunPayoutBatchInput, RunPayoutBatchOutput> = httpsCallable(
  functions,
  'runPayoutBatch',
);

export async function callRunPayoutBatch(
  input: RunPayoutBatchInput,
): Promise<RunPayoutBatchOutput> {
  const parsed = RunPayoutBatchInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('runPayoutBatch.input', parsed.error.flatten());
  }
  const result = await runFn(parsed.data);
  const out = RunPayoutBatchOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('runPayoutBatch.output', out.error.flatten());
  }
  return out.data;
}

// ── listRecentPayouts ──────────────────────────────────────────────────

export const ListRecentPayoutsInputSchema = z.object({
  kind: RecipientKindSchema,
  brandId: z.string().min(1),
  limit: z.number().int().positive().max(200).default(50),
});
export type ListRecentPayoutsInput = z.infer<typeof ListRecentPayoutsInputSchema>;

export const RecentPayoutRowSchema = z.object({
  payoutId: z.string(),
  recipientId: z.string(),
  recipientName: z.string().optional(),
  recipientCode: z.string().optional(),
  grossSen: z.number().int(),
  itemCount: z.number().int().nonnegative(),
  status: z.enum(['pending', 'processing', 'paid', 'failed']),
  method: z.enum(['duitnow', 'bank_transfer']),
  initiatedAtMs: z.number().int().nonnegative(),
  paidAtMs: z.number().int().nonnegative().optional(),
  txRef: z.string().optional(),
  destination: z.object({
    bankName: z.string().optional(),
    accountName: z.string().optional(),
    accountNumber: z.string().optional(),
    duitNowId: z.string().optional(),
  }),
});
export type RecentPayoutRow = z.infer<typeof RecentPayoutRowSchema>;

export const ListRecentPayoutsOutputSchema = z.object({
  payouts: z.array(RecentPayoutRowSchema),
});
export type ListRecentPayoutsOutput = z.infer<typeof ListRecentPayoutsOutputSchema>;

const listFn: HttpsCallable<ListRecentPayoutsInput, ListRecentPayoutsOutput> = httpsCallable(
  functions,
  'listRecentPayouts',
);

export async function callListRecentPayouts(
  input: ListRecentPayoutsInput,
): Promise<ListRecentPayoutsOutput> {
  const parsed = ListRecentPayoutsInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('listRecentPayouts.input', parsed.error.flatten());
  }
  const result = await listFn(parsed.data);
  const out = ListRecentPayoutsOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('listRecentPayouts.output', out.error.flatten());
  }
  return out.data;
}

// ── markPayoutPaid ─────────────────────────────────────────────────────

export const MarkPayoutPaidInputSchema = z.object({
  kind: RecipientKindSchema,
  brandId: z.string().min(1),
  recipientId: z.string().min(1),
  payoutId: z.string().min(1),
  txRef: z.string().max(120).optional(),
});
export type MarkPayoutPaidInput = z.infer<typeof MarkPayoutPaidInputSchema>;

export const MarkPayoutPaidOutputSchema = z.object({
  ok: z.literal(true),
  alreadyPaid: z.boolean(),
});
export type MarkPayoutPaidOutput = z.infer<typeof MarkPayoutPaidOutputSchema>;

const markFn: HttpsCallable<MarkPayoutPaidInput, MarkPayoutPaidOutput> = httpsCallable(
  functions,
  'markPayoutPaid',
);

export async function callMarkPayoutPaid(
  input: MarkPayoutPaidInput,
): Promise<MarkPayoutPaidOutput> {
  const parsed = MarkPayoutPaidInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('markPayoutPaid.input', parsed.error.flatten());
  }
  const result = await markFn(parsed.data);
  const out = MarkPayoutPaidOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('markPayoutPaid.output', out.error.flatten());
  }
  return out.data;
}

// ── requestTenantWithdraw (Sprint 98 — operator-initiated payout) ──────

export const RequestTenantWithdrawInputSchema = z.object({
  tenantId: z.string().min(1),
});
export type RequestTenantWithdrawInput = z.infer<typeof RequestTenantWithdrawInputSchema>;

export const RequestTenantWithdrawOutputSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    payoutId: z.string(),
    grossSen: z.number().int().nonnegative(),
    itemCount: z.number().int().nonnegative(),
  }),
  z.object({
    ok: z.literal(false),
    reason: z.enum(['no_destination', 'nothing_to_pay', 'tenant_inactive']),
    message: z.string(),
  }),
]);
export type RequestTenantWithdrawOutput = z.infer<typeof RequestTenantWithdrawOutputSchema>;

const withdrawFn: HttpsCallable<RequestTenantWithdrawInput, RequestTenantWithdrawOutput> =
  httpsCallable(functions, 'requestTenantWithdraw');

export async function callRequestTenantWithdraw(
  input: RequestTenantWithdrawInput,
): Promise<RequestTenantWithdrawOutput> {
  const parsed = RequestTenantWithdrawInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('requestTenantWithdraw.input', parsed.error.flatten());
  }
  const result = await withdrawFn(parsed.data);
  const out = RequestTenantWithdrawOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('requestTenantWithdraw.output', out.error.flatten());
  }
  return out.data;
}
